<template>
  <Teleport to="body">
    <Transition name="workspace-switcher-overlay">
      <div
        v-if="popupStore.visible"
        class="workspace-switcher-overlay"
        role="presentation"
      >
        <div
          class="
            flex flex-col items-center justify-center min-w-96 min-h-64 p-4
            bg-white rounded-lg shadow-lg dark:bg-ant-dark
          "
        >
          <!-- Icon transition: old out left, new in from right -->
          <div
            class="size-12 relative mb-4"
            :class="{ 'workspace-switcher-icon-animate': iconAnimate }"
          >
            <div
              v-if="popupStore.from"
              class="workspace-switcher-icon workspace-switcher-icon-from"
              :style="iconStyle(popupStore.from)"
            >
              <i
                v-if="popupStore.from.value === 'user'"
                class="fa-regular fa-user fa-lg"
              />
              <i
                v-else
                class="fa-solid fa-building fa-lg"
              />
            </div>
            <div
              v-if="popupStore.to"
              class="workspace-switcher-icon workspace-switcher-icon-to"
              :style="iconStyle(popupStore.to)"
            >
              <i
                v-if="popupStore.to.value === 'user'"
                class="fa-regular fa-user fa-lg"
              />
              <i
                v-else
                class="fa-solid fa-building fa-lg"
              />
            </div>
          </div>
          <p class="m-0 text-sm font-medium text-neutral-500 dark:text-neutral-400">
            Switching to
          </p>
          <p class="my-1 text-lg font-bold wrap-break-words line-height-1.3 text-black dark:text-white">
            {{ popupStore.to?.label ?? 'Unnamed Workspace' }}
          </p>
          <div class="min-h-4 flex items-center justify-center">
            <span class="workspace-switcher-ellipsis">
              <span>.</span><span>.</span><span>.</span>
            </span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useWorkspaceSwitcherPopupStore } from '../../stores/workspaceSwitcherPopup.ts';
import type { WorkspaceOption } from '../../stores/workspaceSwitcherPopup.ts';
import { ref, watch, nextTick } from 'vue';

const popupStore = useWorkspaceSwitcherPopupStore();
const iconAnimate = ref(false);

function iconStyle(opt: WorkspaceOption): Record<string, string> {
  const bg = opt.value === 'user' ? '#10b981' : (opt.color ?? '#10b981');
  return { backgroundColor: bg };
}

watch(
  () => popupStore.visible,
  (visible) => {
    if (visible) {
      iconAnimate.value = false;
      nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              iconAnimate.value = true;
            }, 180);
          });
        });
      });
    } else {
      iconAnimate.value = false;
    }
  },
);
</script>

<style scoped>
.workspace-switcher-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
}

.workspace-switcher-popup {
  gap: 0.75rem;
}

.workspace-switcher-icon {
  position: absolute;
  inset: 0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.workspace-switcher-icon-from {
  transform: translateX(0);
  opacity: 1;
}

.workspace-switcher-icon-to {
  transform: translateX(100%);
  opacity: 0;
}

.workspace-switcher-icon-animate .workspace-switcher-icon-from {
  transform: translateX(-100%);
  opacity: 0;
}

.workspace-switcher-icon-animate .workspace-switcher-icon-to {
  transform: translateX(0);
  opacity: 1;
}

.workspace-switcher-ellipsis {
  display: inline-flex;
  gap: 4px;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--ant-color-text-secondary, #666);
}

.workspace-switcher-ellipsis span {
  animation: workspace-switcher-dot 1.4s ease-in-out infinite both;
}

.workspace-switcher-ellipsis span:nth-child(1) { animation-delay: 0s; }
.workspace-switcher-ellipsis span:nth-child(2) { animation-delay: 0.2s; }
.workspace-switcher-ellipsis span:nth-child(3) { animation-delay: 0.4s; }

@keyframes workspace-switcher-dot {
  0%, 60%, 100% { opacity: 0.3; transform: scale(0.9); }
  30% { opacity: 1; transform: scale(1); }
}

/* Overlay enter/leave */
.workspace-switcher-overlay-enter-active,
.workspace-switcher-overlay-leave-active {
  transition: opacity 0.2s ease;
}

.workspace-switcher-overlay-enter-from,
.workspace-switcher-overlay-leave-to {
  opacity: 0;
}

.workspace-switcher-overlay-enter-active .workspace-switcher-popup,
.workspace-switcher-overlay-leave-active .workspace-switcher-popup {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.workspace-switcher-overlay-enter-from .workspace-switcher-popup,
.workspace-switcher-overlay-leave-to .workspace-switcher-popup {
  transform: scale(0.95);
}
</style>
